/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	faArrowLeft,
	faArrowRight,
	faChevronDown,
	faFolder,
} from "@fortawesome/pro-regular-svg-icons";
import { Autocomplete, TextField } from "@mui/material";

import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { Transforms, BaseEditor, Descendant } from "slate";

import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";

import { useForm } from "react-hook-form";

import { Icon, Modal } from "@/components";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import PromptEditor from "@/pages/Private/pages/List/components/PromptEditor";
import { AiApplyType, CustomColumnTypes, PriorityFilterStatus, UpdateType } from "@/enum/list.enum";

import { TextArea } from "@/components/TextArea/TextArea";
import { InputField } from "@/components/InputField/InputField";
import { ButtonSize, ButtonColor } from "@/components/Button/types";
import { useAppSelector } from "@/redux/hooks";

import suggestionsData from "@/utils/suggestions";
import { Button } from "@/components/Button/Button";

import { CustomColumn, CustomColumnSchema, List } from "@/pages/Private/pages/List/schema/list";

import { ColumnConfig } from "@/pages/Private/pages/List/schema/columnConfig";

import { Toggle } from "@/components/Toggle/Toggle";

import { listSelector } from "@/pages/Private/redux/list/list.slice";

import {
	useRunAiEnrichmentMutation,
	useUpdateListColumnsMutation,
} from "@/pages/Private/redux/list/list.api";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";

import { getSuggestions, replaceWithRealValues } from "../../../utils/column-utils";
import { PromptLibraryModal } from "./PromptLibraryModal";
import { useAiColumnRunCost } from "./useAiColumnRunCost";
import { ColumnTemplate } from "../../../schema/column-template";
import { SaveCustomColumnTemplateModal } from "./SaveCustomColumnTemplateModal";
import { CustomColumnApplyFilters } from "../customColumnApplyFilters";

export interface AiModalProps {
	setShowConfirm: (value: boolean) => void;
	open: boolean;
	listId: number;
	list?: List;
	setOpen: (value: boolean) => void;
	columnId?: number;
	column?: ColumnConfig;
	filters?: CustomColumnApplyFilters;
	customColumnName?: string;
	selectedProspectIds?: number[];
	priorityFilterStatus?: PriorityFilterStatus[];
}

export const AiModal: FunctionComponent<AiModalProps> = ({
	setShowConfirm,
	listId,
	list,
	open,
	setOpen,
	columnId,
	column,
	customColumnName,
	filters,
	selectedProspectIds,
	priorityFilterStatus,
}) => {
	const [showPromptLibrary, setShowPromptLibrary] = useState<boolean>(false);
	const [editorValue, setEditorValue] = useState<Descendant[]>();
	const [editor, setEditor] = useState<BaseEditor>();
	const [aiResult, setAiResult] = useState<string>();

	const [applyTo, setApplyTo] = useState<AiApplyType>(AiApplyType.NONE);
	const [showSaveAsTemplateModal, setShowSaveAsTemplateModal] = useState<boolean>(false);

	const [currentLead, setCurrentLead] = useState<number>(0);

	const [selectedVariable, setSelectedVariable] = useState<
		{ id: string; name: string } | undefined
	>();

	const handlePreviousLead = () => {
		if (currentLead > 0) {
			setCurrentLead(currentLead - 1);
			setAiResult("");
		}
	};

	const handleNextLead = () => {
		if (currentLead < inbox.length - 1) {
			setCurrentLead(currentLead + 1);
			setAiResult("");
		}
	};

	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`aiColumn.${key}`), [t]);

	const [update, { isLoading, error }] = useUpdateListColumnsMutation();
	const [runPrompt, { isLoading: promptIsLoading }] = useRunAiEnrichmentMutation();

	const { inbox } = useAppSelector(listSelector);

	const suggestions = useMemo(() => {
		return inbox[currentLead] ? getSuggestions(inbox[currentLead]) : [];
	}, [currentLead, inbox]);

	const getSuggestionLabel = (key: string) => {
		return suggestionsData.suggestions[key as keyof typeof suggestionsData.suggestions] as string;
	};

	const [selectedPrompt, setSelectedPrompt] = useState<ColumnTemplate | null>(null);

	const {
		handleSubmit,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<CustomColumn>({
		defaultValues: {
			customColumnType: CustomColumnTypes.AI_PROMPT,
			online: false,
			...column,
			customColumnName: column?.name || customColumnName,
			value: column?.config,
		},
		resolver: zodResolver(CustomColumnSchema),
	});

	const handleRunPrompt = async () => {
		const result = await runPrompt({
			id: inbox[currentLead].id,
			prompt: replaceWithRealValues(editorValue, inbox[currentLead], suggestions),
			online: getValues("online") || false,
		}).unwrap();

		setAiResult(result.data);
	};

	useEffect(() => {
		if (column) {
			setEditorValue(JSON.parse(column.config));
			setEditor(JSON.parse(column.config));
		}
	}, [column]);

	const serialize = (nodes: any) => {
		return JSON.stringify(nodes);
	};

	const onSubmit = async (values: CustomColumn) => {
		try {
			values.value = editorValue ? serialize(editorValue) : "";

			const prospectIds =
				applyTo === AiApplyType.FIRST_10
					? inbox.slice(0, 10).map((p) => p.id)
					: applyTo === AiApplyType.CURRENTLY_SELECTED
						? selectedProspectIds
						: undefined;

			await update({
				...values,
				updateType: UpdateType.CUSTOM_COLUMN,
				aiApplyType: applyTo,
				prospectIds: prospectIds,
				id: listId,
				columnId,
				filters: applyTo === AiApplyType.CURRENTLY_FILTERED ? filters : undefined,
				priorityFilterStatus: priorityFilterStatus ?? undefined,
			}).unwrap();

			setOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const applyToButtonText = useMemo(() => {
		return applyTo === AiApplyType.NONE
			? ts(`button.${AiApplyType.NONE}`)
			: applyTo === AiApplyType.FIRST_10
				? ts(`button.${AiApplyType.FIRST_10}`)
				: applyTo === AiApplyType.CURRENTLY_FILTERED
					? ts(`button.${AiApplyType.CURRENTLY_FILTERED}`)
					: ts(`button.${AiApplyType.ALL}`);
	}, [applyTo, ts]);

	// Rows the column runs on for the Apply-to choice, quoted beside Save (PREC-4263).
	const cost = useAiColumnRunCost({ open, applyTo, selectedProspectIds, filters });

	return (
		<Modal
			handleClose={() => {
				setShowConfirm(true);
			}}
			disableSave={!cost.canConfirm}
			handleSave={handleSubmit(onSubmit)}
			handleSecondSave={() => setShowSaveAsTemplateModal(true)}
			isLoading={isLoading}
			isOpened={open}
			secondSubmitButtonText="Save Prompt Template"
			size="lg"
			submitButtonText={applyToButtonText}
			title="Create AI Column"
		>
			<div className="text-ssm font-medium mb-2">
				<InputField
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						setValue("customColumnName", event.target.value, {
							shouldValidate: true,
							shouldDirty: true,
						});
					}}
					label="Column Name"
					name={"name"}
					placeholder="Enter column name"
					value={getValues("customColumnName")}
				/>
			</div>

			<div className="flex flex-col items-start">
				<div className="flex flex-row justify-between w-full">
					<div className="text-ssm font-medium mt-2 mb-1">Prompt editor</div>
					<div className="flex flex-row gap-2">
						<div className="text-ssm font-medium mt-2 mb-1 flex items-center">
							<Toggle
								isChecked={getValues("online") || false}
								onChange={() => {
									setValue("online", !getValues("online"), {
										shouldValidate: true,
										shouldDirty: true,
									});
								}}
							/>{" "}
							<span className="ml-2">Activate web search</span>
						</div>
					</div>
				</div>

				<div className="flex relative flex-col w-full border border-gray-300 rounded-xl">
					<div className="w-full min-h-[120px] ">
						{!selectedPrompt && (
							<PromptEditor
								getSuggestionLabel={getSuggestionLabel}
								handleChange={(value) => {
									setEditor(value);
								}}
								handleValueChange={(value) => {
									setEditorValue(value);
								}}
								suggestions={suggestions}
								value={column ? JSON.parse(column.config) : undefined}
							/>
						)}
						{selectedPrompt && (
							<PromptEditor
								getSuggestionLabel={getSuggestionLabel}
								handleChange={(value) => {
									setEditor(value);
								}}
								handleValueChange={(value) => {
									setEditorValue(value);
								}}
								suggestions={suggestions}
								value={selectedPrompt ? JSON.parse(selectedPrompt.config) : undefined}
							/>
						)}
					</div>
					<div className="p-2 w-full flex border-t border-gray-300 justify-between items-center">
						<div className="flex items-center">
							<div className="mr-2 text-sm text-gray-700">Select</div>
							<Autocomplete
								getOptionLabel={(option: { name: string; id: string }) => option.name}
								id="event"
								options={
									suggestions
										?.filter((key) => {
											const label = getSuggestionLabel(key);

											return label && label !== key;
										})
										?.map((key) => ({
											name: getSuggestionLabel(key) as string,
											id: key,
										})) || []
								}
								popupIcon={
									<Icon className="text-gray-500 h-[24px] w-[15px]" icon={faChevronDown} />
								}
								renderInput={(params) => {
									return (
										<TextField
											// sx={{
											// 	height: "40px",
											// }}
											{...params}
											placeholder="Variable"
										/>
									);
								}}
								size="small"
								sx={{
									width: "300px",
									"& .MuiFilledInput-root": {
										paddingTop: "4px!important",
										paddingBottom: "4px!important",
										marginBottom: "0px!important",
									},
									"&.MuiFormControl-root": {
										height: "40px",
									},
								}}
								value={selectedVariable}
								onChange={(event, value) => {
									if (value && editor) {
										const mentionNode = {
											type: "mention" as const,
											character: `${value.id}`,
											children: [
												{
													text: ``,
												},
											],
										};

										// Insert the mention node at the current selection
										Transforms.insertNodes(editor as any, mentionNode);
										// Add a space after the mention
										Transforms.insertText(editor as any, " ");

										setSelectedVariable(undefined);
									}
								}}
							/>
							<div className="ml-2 text-sm text-gray-700">
								or enter{" "}
								<span className="bg-inactive-item px-1 border border-gray-200 rounded-md">
									{"{"}
								</span>{" "}
								to insert variable
							</div>
						</div>
						<div className="w-1/2 flex justify-end">
							<div className="w-1/2 mr-2 max-w-[150px]">
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									disabled={false}
									image={<Icon className="mr-2" icon={faFolder} />}
									size={ButtonSize.S}
									title="Prompt library"
									onClick={() => {
										setShowPromptLibrary(true);
									}}
								/>
							</div>
							<div className="max-w-[120px]">
								<Button
									disabled={false}
									isLoading={promptIsLoading}
									size={ButtonSize.S}
									title="Test prompt"
									onClick={handleRunPrompt}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="flex flex-row">
				<div className="flex flex-col grow mr-2">
					<div className="text-ssm font-medium mt-4 mb-2">Prompt preview</div>
					<TextArea
						className="!mb-0 grow mr-4 max-h-[192px]"
						name={"randomValue"}
						placeholder="Enter value"
						showError={false}
						value={replaceWithRealValues(editorValue, inbox[currentLead], suggestions) || ""}
					/>
				</div>
				<div className="flex flex-col grow ml-2">
					<div className="text-ssm font-medium mt-4 mb-2">Prompt result preview</div>
					<TextArea
						className="!mb-0 grow mr-4 max-h-[192px]"
						name={"randomValue"}
						showError={false}
						value={aiResult || ""}
					/>
				</div>
			</div>

			<div className="flex flex-row mt-5 border-t pt-5 border-gray-200">
				<div className="w-1/2 mr-2 grow">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={currentLead === 0}
						image={<Icon className="mr-2" icon={faArrowLeft} />}
						size={ButtonSize.S}
						title="Previous lead"
						onClick={handlePreviousLead}
					/>
				</div>
				<div className="w-1/2 ml-2 grow">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={currentLead === inbox.length - 1}
						iconRight={true}
						image={<Icon className="ml-2" icon={faArrowRight} />}
						size={ButtonSize.S}
						title="Next lead"
						onClick={handleNextLead}
					/>
				</div>
			</div>
			<div className="flex flex-row mt-5 border-t pt-5 border-gray-200">
				<Dropdown
					data={[
						{
							title: ts(AiApplyType.NONE),
							id: AiApplyType.NONE,
						},
						{
							title: ts(AiApplyType.FIRST_10),
							id: AiApplyType.FIRST_10,
						},
						{
							title: ts(AiApplyType.CURRENTLY_FILTERED),
							id: AiApplyType.CURRENTLY_FILTERED,
						},
						{
							title: ts(AiApplyType.CURRENTLY_SELECTED),
							id: AiApplyType.CURRENTLY_SELECTED,
						},
						{
							title: ts(AiApplyType.ALL),
							id: AiApplyType.ALL,
						},
						{
							title: ts(AiApplyType.ALL_INCLUDING_ASSIGNED),
							id: AiApplyType.ALL_INCLUDING_ASSIGNED,
						},
					]}
					floating={false}
					handleSelect={function (value?: AutoCompleteItem) {
						if (value?.id) {
							setApplyTo(value.id as AiApplyType);
						}
					}}
					label="Apply to ..."
					value={applyTo ? { title: ts(applyTo), id: applyTo } : undefined}
				/>
			</div>
			{cost.block}

			{getAllErrors(error, formErrors).length ? (
				<div className="mt-4 mb-0">{renderErrorMessages(getAllErrors(error, formErrors))}</div>
			) : null}
			{showPromptLibrary && (
				<PromptLibraryModal
					isOpen={showPromptLibrary}
					selectPrompt={async (prompt) => {
						setSelectedPrompt(null);
						await new Promise((resolve) => setTimeout(resolve, 1));
						setSelectedPrompt(prompt);
						setEditorValue(JSON.parse(prompt.config));
						setEditor(JSON.parse(prompt.config));
					}}
					onClose={() => setShowPromptLibrary(false)}
				/>
			)}
			{showSaveAsTemplateModal && list && (
				<SaveCustomColumnTemplateModal
					editor={editorValue ? serialize(editorValue) : ""}
					initialListId={list.id}
					isOpen={showSaveAsTemplateModal}
					listSourceType={list.sourceType}
					type={CustomColumnTypes.AI_PROMPT}
					onClose={() => setShowSaveAsTemplateModal(false)}
				/>
			)}
		</Modal>
	);
};
